import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import {
  Building2, Plus, UserPlus, Trash2, Loader2, CheckCircle2,
  Package, MoreVertical, Pencil, Star,
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const AVATAR_COLORS = [
  'bg-purple-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-pink-500',
];

const avatarColor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash << 5) - hash + seed.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const initials = (name = '', email = '') => {
  const base = (name || email || '?').trim();
  if (!base) return '?';
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

// ——— Members stack (avatars overlapping) ———
const MembersStack = ({ members = [], onInvite }) => {
  const visible = members.slice(0, 3);
  const extra = Math.max(0, members.length - visible.length);
  return (
    <div className="flex items-center -space-x-2">
      {visible.map((m, i) => (
        <div
          key={m.user_id || i}
          title={m.name || m.email}
          className={`w-8 h-8 rounded-full ${avatarColor(m.email || m.name || '?')} ring-2 ring-card flex items-center justify-center text-[11px] font-semibold text-white`}
        >
          {initials(m.name, m.email)}
        </div>
      ))}
      {extra > 0 && (
        <div className="w-8 h-8 rounded-full ring-2 ring-card bg-secondary flex items-center justify-center text-[11px] font-medium text-foreground">
          +{extra}
        </div>
      )}
      <button
        type="button"
        onClick={onInvite}
        className="w-8 h-8 rounded-full ring-2 ring-card border-2 border-dashed border-primary/50 text-primary flex items-center justify-center hover:bg-primary/10 transition-colors"
        title="Invite collaborator"
        data-testid="org-card-invite-btn"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ——— Single Organization Card (Google-Cloud-style) ———
const OrgCard = ({ org, isCurrent, canModify, canDelete, onInvite, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const projectCount = org.projects?.length ?? org.project_count ?? 0;
  const visibleProjects = expanded ? (org.projects || []) : (org.projects || []).slice(0, 3);
  return (
    <Card
      className="bg-card border-border hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all rounded-xl overflow-hidden flex flex-col"
      data-testid={`org-card-${org.id}`}
    >
      {/* Header */}
      <div className="p-5 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-foreground truncate" data-testid={`org-card-name-${org.id}`}>
              {org.name}
            </h3>
            {org.is_default && (
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 shrink-0" />
            )}
            {isCurrent && (
              <Badge variant="outline" className="text-[10px] h-5 shrink-0">You</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {org.is_default ? 'Default Organization' : (org.owner?.email || 'Organization')}
          </p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Org actions"
              data-testid={`org-card-menu-${org.id}`}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={onInvite} data-testid={`org-menu-invite-${org.id}`}>
              <UserPlus className="w-4 h-4 mr-2" />Invite Collaborator
            </DropdownMenuItem>
            {canModify && (
              <DropdownMenuItem onClick={onEdit} data-testid={`org-menu-modify-${org.id}`}>
                <Pencil className="w-4 h-4 mr-2" />Modify
              </DropdownMenuItem>
            )}
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive focus:bg-destructive/10"
                  data-testid={`org-menu-delete-${org.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Body – projects preview (takes the middle) */}
      <div className="px-5 flex-1 min-h-[88px]">
        {org.projects && org.projects.length > 0 ? (
          <div className="space-y-1.5">
            {visibleProjects.map((p) => (
              <div
                key={p.project_id}
                className="flex items-center gap-2 text-xs text-muted-foreground truncate"
                data-testid={`org-card-${org.id}-project-${p.project_id}`}
              >
                <Package className={`w-3 h-3 shrink-0 ${p.is_default ? 'text-amber-400' : 'text-primary'}`} />
                <span className="truncate" title={p.name || p.project_id}>
                  {p.name || p.project_id}
                </span>
                {p.is_default && (
                  <Badge variant="outline" className="text-[9px] h-4 px-1 ml-1 border-amber-700/40 text-amber-400">
                    default
                  </Badge>
                )}
                {p.source === 'migration' && (
                  <Badge variant="outline" className="text-[9px] h-4 px-1 ml-1 text-muted-foreground">
                    migration
                  </Badge>
                )}
              </div>
            ))}
            {projectCount > 3 && (
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="text-[11px] text-primary hover:underline pt-0.5"
                data-testid={`org-card-toggle-${org.id}`}
              >
                {expanded ? 'Show less' : `+${projectCount - 3} more`}
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground/70 italic">No projects yet</p>
        )}
      </div>

      {/* Footer: Projects count + Members stack */}
      <div className="px-5 py-4 mt-3 border-t border-border/60 flex items-end justify-between">
        <div className="flex flex-col items-start" data-testid={`org-card-projects-${org.id}`}>
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-muted-foreground" />
            <span className="text-xl font-semibold">{projectCount}</span>
          </div>
          <span className="text-[11px] text-muted-foreground mt-1">Projects</span>
        </div>

        <div className="flex flex-col items-end">
          <MembersStack members={org.members_preview || []} onInvite={onInvite} />
          <span className="text-[11px] text-muted-foreground mt-1">
            {org.member_count} Member{org.member_count === 1 ? '' : 's'}
          </span>
        </div>
      </div>
    </Card>
  );
};

const Organization = () => {
  const [currentOrg, setCurrentOrg] = useState(null);
  const [hasOrg, setHasOrg] = useState(false);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [allOrgs, setAllOrgs] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState('viewer');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentOrgProjects, setCurrentOrgProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);      // { id, name }
  const [orgInvite, setOrgInvite] = useState(null);  // { org_id, org_name, email, role }
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);

  const [newOrg, setNewOrg] = useState({ name: '' });

  // Project-tab state — new project creation directly under Organizations
  const [activeTab, setActiveTab] = useState('orgs');
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', org_id: '' });
  const [creatingProject, setCreatingProject] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      try {
        const me = await axios.get(`${API}/auth/me`);
        setCurrentUserRole(me.data?.role || 'viewer');
        setCurrentUserId(me.data?.user_id || null);
      } catch (_) { /* noop */ }

      const orgRes = await axios.get(`${API}/org/current`);
      setHasOrg(orgRes.data.has_org);
      setCurrentOrg(orgRes.data.org);

      if (orgRes.data.has_org) {
        const memRes = await axios.get(`${API}/org/members`);
        setCurrentMembers(memRes.data.members || []);
      } else {
        setCurrentMembers([]);
      }

      // Fetch projects belonging to the user's current org so non-super_admins
      // can still see the projects under their organization card.
      try {
        const projRes = await axios.get(`${API}/projects`);
        const orgId = orgRes.data?.org?.id;
        const list = (projRes.data?.projects || []).filter(
          (p) => !orgId || p.org_id === orgId
        );
        setCurrentOrgProjects(list);
      } catch (_) {
        setCurrentOrgProjects([]);
      }

      try {
        const allRes = await axios.get(`${API}/org/list`);
        setAllOrgs(allRes.data.organizations || []);
      } catch (e) {
        if (e.response?.status !== 403) console.error(e);
        setAllOrgs([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const createOrg = async () => {
    try {
      await axios.post(`${API}/org/create`, { name: newOrg.name, plan: 'free' });
      toast.success('Organization created');
      setCreateOpen(false);
      setNewOrg({ name: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create organization'); }
  };

  const saveEditOrg = async () => {
    if (!editOrg?.name?.trim()) return;
    setSavingEdit(true);
    try {
      const res = await axios.put(`${API}/org/${editOrg.id}`, { name: editOrg.name.trim(), plan: 'free' });
      console.log('[org] PUT success', res.data);
      toast.success(`Renamed to "${editOrg.name.trim()}"`);
      setEditOrg(null);
      await fetchData();
    } catch (e) {
      console.error('[org] PUT failed', e);
      toast.error(e.response?.data?.detail || 'Failed to update organization');
    } finally {
      setSavingEdit(false);
    }
  };

  const createProject = async () => {
    const name = (newProject.name || '').trim();
    if (!name) return;
    setCreatingProject(true);
    try {
      await axios.post(`${API}/projects`, {
        name,
        description: newProject.description || '',
        org_id: newProject.org_id || currentOrg?.id || undefined,
      });
      toast.success(`Project "${name}" created`);
      setCreateProjectOpen(false);
      setNewProject({ name: '', description: '', org_id: '' });
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };


  const confirmDeleteOrg = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      const res = await axios.delete(`${API}/org/${deleteTarget.id}`);
      console.log('[org] DELETE success', res.data);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
      await fetchData();
    } catch (e) {
      console.error('[org] DELETE failed', e);
      toast.error(e.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setDeletingId(null);
    }
  };

  const sendOrgInvite = async () => {
    if (!orgInvite?.email) return;
    setSendingInvite(true);
    try {
      await axios.post(`${API}/org/${orgInvite.org_id}/invite`, {
        email: orgInvite.email.trim(),
        role: orgInvite.role,
      });
      toast.success(`Invited ${orgInvite.email} to ${orgInvite.org_name}`);
      setOrgInvite(null);
      await fetchData();
    } catch (e) {
      console.error('[org] INVITE failed', e);
      toast.error(e.response?.data?.detail || 'Failed to invite');
    } finally {
      setSendingInvite(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="org-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const isSuperAdmin = currentUserRole === 'super_admin';

  // Build the grid of cards.
  // super_admin → allOrgs (enriched with members_preview when it matches their own org)
  // non-super → [currentOrg] only, if it exists
  let orgsToRender = [];
  if (isSuperAdmin && allOrgs.length > 0) {
    orgsToRender = allOrgs.map((o) => ({
      ...o,
      members_preview: o.id === currentOrg?.id ? currentMembers : (o.owner ? [o.owner] : []),
    }));
  } else if (hasOrg && currentOrg) {
    orgsToRender = [{
      id: currentOrg.id,
      name: currentOrg.name,
      is_default: !!currentOrg.is_default,
      owner: currentMembers.find((m) => m.user_id === currentOrg.owner_user_id) || null,
      owner_user_id: currentOrg.owner_user_id,
      member_count: currentMembers.length,
      project_count: currentOrgProjects.length,
      projects: currentOrgProjects,
      members_preview: currentMembers,
    }];
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="org-page">
      {/* Top action bar (matches reference: button on the left) */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-['Chivo']" data-testid="org-title">Organizations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage organizations, members and associated projects.
          </p>
        </div>
        {(isSuperAdmin || !hasOrg) && (
          <Button
            onClick={() => setCreateOpen(true)}
            className="shadow-sm"
            data-testid="org-create-btn-header"
          >
            <Plus className="w-4 h-4 mr-1.5" />New Organization
          </Button>
        )}
      </div>

      <div className="h-px bg-border/60" />

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="org-tabs">
        <TabsList className="bg-secondary/40 border border-border" data-testid="org-tabs-list">
          <TabsTrigger value="orgs" data-testid="org-tab-orgs">Organizations</TabsTrigger>
          <TabsTrigger value="projects" data-testid="org-tab-projects">
            Projects
            <Badge variant="outline" className="ml-2 text-[10px]">{currentOrgProjects.length}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orgs" className="mt-4 space-y-4">
          {/* Empty state */}
          {orgsToRender.length === 0 && (
            <div
              className="flex flex-col items-center justify-center py-20 text-center"
              data-testid="org-empty-state"
            >
              <Building2 className="w-14 h-14 text-primary/40 mb-4" />
              <h2 className="text-xl font-semibold">No organizations yet</h2>
              <p className="text-muted-foreground mt-1 max-w-md">
                Create an organization to set up a workspace, invite teammates, and group projects together.
              </p>
              <Button className="mt-6" onClick={() => setCreateOpen(true)} data-testid="org-create-btn-empty">
                <Plus className="w-4 h-4 mr-2" />Create Organization
              </Button>
            </div>
          )}

          {/* Org card grid */}
          {orgsToRender.length > 0 && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
              data-testid="org-card-grid"
            >
              {orgsToRender.map((o) => {
                const isCurrent = o.id === currentOrg?.id;
                const canModify = isSuperAdmin || (isCurrent && ['admin', 'super_admin'].includes(currentUserRole));
                const canDelete = isSuperAdmin && !o.is_default;
                return (
                  <OrgCard
                    key={o.id}
                    org={o}
                    isCurrent={isCurrent}
                    canModify={canModify}
                    canDelete={canDelete}
                    onInvite={() => setOrgInvite({ org_id: o.id, org_name: o.name, email: '', role: 'viewer' })}
                    onEdit={() => setEditOrg({ id: o.id, name: o.name })}
                    onDelete={() => setDeleteTarget({ id: o.id, name: o.name })}
                  />
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="projects" className="mt-4 space-y-4" data-testid="projects-tab-content">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-base font-semibold">Projects in {currentOrg?.name || 'your organisation'}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Projects scope your saved IaC, blueprints and version history. Pick one from the
                workspace switcher to start working in it.
              </p>
            </div>
            {hasOrg && (
              <Button
                onClick={() => {
                  setNewProject({ name: '', description: '', org_id: currentOrg?.id || '' });
                  setCreateProjectOpen(true);
                }}
                data-testid="project-create-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />New Project
              </Button>
            )}
          </div>

          {currentOrgProjects.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-16 text-center rounded-lg border border-dashed border-border"
              data-testid="projects-empty-state"
            >
              <Package className="w-12 h-12 text-primary/40 mb-3" />
              <h3 className="text-base font-semibold">No projects in this organisation yet</h3>
              <p className="text-muted-foreground text-sm mt-1 max-w-md">
                Create your first project to start saving IaC configurations, blueprints and
                pipeline templates here.
              </p>
              {hasOrg && (
                <Button
                  className="mt-5"
                  onClick={() => {
                    setNewProject({ name: '', description: '', org_id: currentOrg?.id || '' });
                    setCreateProjectOpen(true);
                  }}
                  data-testid="project-create-btn-empty"
                >
                  <Plus className="w-4 h-4 mr-2" />Create Project
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
              {currentOrgProjects.map((p) => (
                <Card
                  key={p.project_id}
                  className="p-4 hover:border-primary/40 transition-colors"
                  data-testid={`project-card-${p.project_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-md bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                      <Package className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm truncate">{p.name}</h4>
                        {p.is_default && (
                          <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-300 bg-amber-500/5">
                            DEFAULT
                          </Badge>
                        )}
                      </div>
                      {p.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground/70 mt-2 font-mono truncate">
                        {p.project_id}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { window.location.href = `/projects/${p.project_id}`; }}
                      data-testid={`project-open-${p.project_id}`}
                    >
                      Open →
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Org Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border-border" data-testid="org-create-dialog">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>Set up a workspace for your team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Organization Name</Label>
              <Input
                value={newOrg.name}
                onChange={(e) => setNewOrg({ name: e.target.value })}
                placeholder="Acme Corp"
                className="bg-secondary border-border mt-1 text-foreground"
                data-testid="org-create-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createOrg} disabled={!newOrg.name.trim()} data-testid="org-create-submit">
              <CheckCircle2 className="w-4 h-4 mr-2" />Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Org Dialog */}
      <Dialog open={!!editOrg} onOpenChange={(v) => !v && setEditOrg(null)}>
        <DialogContent className="bg-card border-border" data-testid="org-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modify Organization</DialogTitle>
            <DialogDescription>Update the organization name.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Organization Name</Label>
              <Input
                value={editOrg?.name || ''}
                onChange={(e) => setEditOrg((p) => ({ ...p, name: e.target.value }))}
                className="bg-secondary border-border mt-1 text-foreground"
                data-testid="org-edit-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOrg(null)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={saveEditOrg} disabled={!editOrg?.name?.trim() || savingEdit} data-testid="org-edit-submit">
              {savingEdit ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" />Save</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation AlertDialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border" data-testid="org-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-semibold text-foreground">"{deleteTarget?.name}"</span>{' '}
              and remove all member associations. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId} data-testid="org-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteOrg}
              disabled={!!deletingId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="org-delete-confirm"
            >
              {deletingId ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" />Delete</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Collaborator Dialog */}
      <Dialog open={!!orgInvite} onOpenChange={(v) => !v && setOrgInvite(null)}>
        <DialogContent className="bg-card border-border" data-testid="org-invite-collab-dialog">
          <DialogHeader>
            <DialogTitle>Invite Collaborator</DialogTitle>
            <DialogDescription>
              Invite a user to join{' '}
              <span className="font-semibold text-foreground">{orgInvite?.org_name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Email</Label>
              <Input
                value={orgInvite?.email || ''}
                onChange={(e) => setOrgInvite((p) => ({ ...p, email: e.target.value }))}
                placeholder="colleague@company.com"
                className="bg-secondary border-border mt-1 text-foreground"
                data-testid="org-invite-collab-email-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={orgInvite?.role || 'viewer'}
                onValueChange={(v) => setOrgInvite((p) => ({ ...p, role: v }))}
              >
                <SelectTrigger className="bg-secondary border-border mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer (read-only)</SelectItem>
                  <SelectItem value="operator">Operator (read + write)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrgInvite(null)} disabled={sendingInvite}>Cancel</Button>
            <Button onClick={sendOrgInvite} disabled={!orgInvite?.email || sendingInvite} data-testid="org-invite-collab-submit">
              {sendingInvite ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
              ) : (
                <><UserPlus className="w-4 h-4 mr-2" />Send Invite</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={createProjectOpen} onOpenChange={setCreateProjectOpen}>
        <DialogContent className="bg-card border-border" data-testid="project-create-dialog">
          <DialogHeader>
            <DialogTitle>New Project</DialogTitle>
            <DialogDescription>
              Projects scope your saved IaC, blueprints and pipeline history under the current organisation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="project-name">Project name</Label>
              <Input
                id="project-name"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="e.g. checkout-staging"
                className="bg-secondary border-border mt-1 text-foreground"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <Label htmlFor="project-desc">Description (optional)</Label>
              <Textarea
                id="project-desc"
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Short summary of what this project will contain."
                className="bg-secondary border-border mt-1 text-foreground"
                rows={3}
                data-testid="project-desc-input"
              />
            </div>
            {isSuperAdmin && allOrgs.length > 0 ? (
              <div>
                <Label>Organisation</Label>
                <Select
                  value={newProject.org_id || currentOrg?.id || ''}
                  onValueChange={(v) => setNewProject({ ...newProject, org_id: v })}
                >
                  <SelectTrigger className="bg-secondary border-border mt-1" data-testid="project-org-select">
                    <SelectValue placeholder="Pick an organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    {allOrgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}{o.is_default ? ' (default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                Will be created under <span className="font-semibold text-foreground">{currentOrg?.name || 'your organisation'}</span>.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateProjectOpen(false)} disabled={creatingProject}>
              Cancel
            </Button>
            <Button onClick={createProject} disabled={!newProject.name.trim() || creatingProject} data-testid="project-create-submit">
              {creatingProject ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />Create Project</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Organization;
